import React from 'react';
import { X, FileText, Database, List, MessageSquare, Zap, CheckCircle } from 'lucide-react';
import { TokenUsagePie } from './TokenUsagePie';

interface ChatHeaderProps {
  taskName: string;
  taskStatus: string;
  streamName: string;
  sessionTokens: number;
  autoApprove: boolean;
  viewMode: 'chat' | 'timeline';
  onClose: () => void;
  onShowDetails?: () => void;
  onShowContext?: () => void;
  onSetAutoApprove: (value: boolean) => void;
  onSetViewMode: (mode: 'chat' | 'timeline') => void;
  onMarkComplete?: () => void;
}

/**
 * Component for rendering the task chat header
 */
export function ChatHeader({
  taskName,
  taskStatus,
  streamName,
  sessionTokens,
  autoApprove,
  viewMode,
  onClose,
  onShowDetails,
  onShowContext,
  onSetAutoApprove,
  onSetViewMode,
  onMarkComplete,
}: ChatHeaderProps) {
  return (
    <div className="flex-shrink-0 border-b border-surface-600 p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="font-semibold text-text-primary truncate">{taskName}</h2>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              taskStatus === 'running' ? 'bg-primary-500/20 text-primary-400' :
              taskStatus === 'attention' ? 'bg-warning-500/20 text-warning-400' :
              taskStatus === 'completed' ? 'bg-success-500/20 text-success-400' :
              'bg-surface-600 text-text-muted'
            }`}>
              {taskStatus}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <p className="text-sm text-text-muted">{streamName}</p>
            <TokenUsagePie sessionTokens={sessionTokens} />
          </div>
        </div>
        <div className="flex items-center gap-1">
          {/* Auto-approve Toggle */}
          <button
            onClick={() => onSetAutoApprove(!autoApprove)}
            className={`flex items-center gap-1 px-2 py-1 rounded text-xs mr-2 transition-colors ${
              autoApprove
                ? 'bg-warning-500/20 text-warning-400'
                : 'bg-surface-700 text-text-muted hover:text-text-primary'
            }`}
            title={autoApprove ? 'Auto-approve is ON - status changes will be applied automatically' : 'Auto-approve is OFF - status changes require confirmation'}
          >
            <Zap className="w-3 h-3" />
            {autoApprove ? 'Auto' : 'Manual'}
          </button>

          {/* View Toggle */}
          <div className="flex items-center bg-surface-800 rounded-lg p-0.5 mr-2">
            <button
              onClick={() => onSetViewMode('chat')}
              className={`p-1.5 rounded transition-colors ${
                viewMode === 'chat'
                  ? 'bg-surface-700 text-text-primary'
                  : 'text-text-muted hover:text-text-primary'
              }`}
              title="Chat View"
            >
              <MessageSquare className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onSetViewMode('timeline')}
              className={`p-1.5 rounded transition-colors ${
                viewMode === 'timeline'
                  ? 'bg-surface-700 text-text-primary'
                  : 'text-text-muted hover:text-text-primary'
              }`}
              title="Timeline View"
            >
              <List className="w-3.5 h-3.5" />
            </button>
          </div>
          {onShowDetails && (
            <button
              onClick={onShowDetails}
              className="p-1.5 text-text-muted hover:text-text-primary hover:bg-surface-700 rounded transition-colors"
              title="Task Details"
            >
              <FileText className="w-4 h-4" />
            </button>
          )}
          {onShowContext && (
            <button
              onClick={onShowContext}
              className="p-1.5 text-text-muted hover:text-text-primary hover:bg-surface-700 rounded transition-colors"
              title="Context Store"
            >
              <Database className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1.5 text-text-muted hover:text-text-primary hover:bg-surface-700 rounded transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Mark complete button - shown when task is in attention state */}
      {taskStatus === 'attention' && onMarkComplete && (
        <div className="mt-3">
          <button
            onClick={onMarkComplete}
            className="flex items-center gap-2 px-3 py-2 bg-success-500/20 hover:bg-success-500/30 text-success-400 rounded-lg text-sm font-medium transition-colors"
          >
            <CheckCircle className="w-4 h-4" />
            Mark complete
          </button>
        </div>
      )}
    </div>
  );
}
